import { useState } from 'react';
import { ArrowRight, Check, ArrowLeft, Loader2, AlertCircle, Eye, EyeOff, Shield } from 'lucide-react';

type Props = {
  onSuccess: (email: string, agencyName: string, password: string) => void;
  onBack?: () => void;
};

type Interval = 'monthly' | 'yearly';

const FEATURES = [
  'Unlimited applications',
  'Custom screening form builder',
  'Full applicant dashboard',
  'CV upload & storage',
  'Email notifications',
  'Shareable link for any platform',
  '7-day money-back guarantee',
];

export default function CheckoutPage({ onBack }: Props) {
  const [email, setEmail] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [interval, setInterval] = useState<Interval>('yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');

  const price = interval === 'monthly' ? 50 : 480;
  const priceLabel = interval === 'monthly' ? '$50/month' : '$480/year';

  const pwScore = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const pwLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore];
  const pwColor = ['bg-ink-400', 'bg-ink-400', 'bg-ink-500', 'bg-ink-700', 'bg-ink-900'][pwScore];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setCheckoutUrl('');

    if (!email.trim() || !agencyName.trim() || !password.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (agencyName.trim().length < 2) {
      setError('Agency name must be at least 2 characters.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const origin = window.location.origin;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email: email.trim(),
            agencyName: agencyName.trim(),
            interval,
            successUrl: `${origin}/?payment=success&email=${encodeURIComponent(email.trim())}&agency=${encodeURIComponent(agencyName.trim())}&pw=${encodeURIComponent(password)}`,
            cancelUrl: `${origin}/?payment=cancelled`,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to start checkout');
      }

      const data = await res.json();

      const popup = window.open(data.url, '_blank');
      if (!popup) {
        setCheckoutUrl(data.url);
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-950 flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-5xl">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-ink-400 hover:text-white text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: plan summary */}
          <div className="text-white hidden lg:block">
            <div className="flex items-center gap-2.5 mb-12">
              <span className="font-bold text-lg tracking-tight">CareHire</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-[1.1] tracking-tightest text-balance">
              Start hiring in minutes.
            </h1>
            <p className="text-ink-400 text-lg mb-12 max-w-md leading-relaxed">
              One simple plan. Everything included. Cancel anytime.
            </p>

            <div className="space-y-4">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-3 border-t border-ink-800 pt-4">
                  <Check className="w-4 h-4 text-white flex-shrink-0" strokeWidth={2} />
                  <span className="text-ink-300 text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: checkout form */}
          <div className="bg-white rounded-lg shadow-2xl p-8 lg:p-10">
            {/* Mobile logo */}
            <div className="flex items-center gap-2.5 mb-8 lg:hidden">
              <span className="font-bold text-ink-900">CareHire</span>
            </div>

            <h2 className="text-2xl font-bold text-ink-900 mb-2 tracking-tight">Start with payment</h2>
            <p className="text-ink-500 text-sm mb-8">
              Pay securely first, then your account is created automatically. No separate sign-up needed.
            </p>

            {/* Billing cycle toggle */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-ink-700 mb-2">Billing cycle</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setInterval('monthly')}
                  className={`p-4 rounded-md border-2 text-left transition-colors ${
                    interval === 'monthly'
                      ? 'border-ink-900 bg-ink-50'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <span className="block text-sm font-semibold text-ink-900">Monthly</span>
                  <span className="block text-xs text-ink-500 mt-0.5">$50/month</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInterval('yearly')}
                  className={`p-4 rounded-md border-2 text-left transition-colors relative ${
                    interval === 'yearly'
                      ? 'border-ink-900 bg-ink-50'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <span className="absolute top-0 right-0 -mt-px -mr-px bg-ink-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-md rounded-tr-md uppercase">
                    Save 20%
                  </span>
                  <span className="block text-sm font-semibold text-ink-900">Yearly</span>
                  <span className="block text-xs text-ink-500 mt-0.5">$480/year</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com"
                  required
                  className="w-full px-4 py-3 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1.5">Agency name</label>
                <input
                  type="text"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  placeholder="e.g. Sunrise Care Agency"
                  required
                  className="w-full px-4 py-3 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-11 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full ${i < pwScore ? pwColor : 'bg-ink-100'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-ink-400">{pwLabel} — use 8+ characters with a mix of letters, numbers, and symbols</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-11 rounded-md border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent"
                  />
                </div>
                {confirmPassword.length > 0 && password !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>

              {/* Price summary */}
              <div className="flex items-baseline justify-between p-4 bg-ink-50 rounded-md border border-ink-100">
                <span className="text-sm text-ink-500">Total due today</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-ink-900 tracking-tightest">${price}</span>
                  <span className="text-ink-500 text-sm ml-1">{interval === 'monthly' ? '/month' : '/year'}</span>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 p-3 rounded-md bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {checkoutUrl && (
                <div className="flex items-start gap-2.5 p-3 rounded-md bg-ink-50 border border-ink-100">
                  <Check className="w-4 h-4 text-ink-900 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-ink-700">
                    <p className="mb-1">Checkout opened in a new tab. If it didn't open, click the button below:</p>
                    <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-ink-900 font-semibold underline">
                      Open Stripe checkout
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 bg-ink-900 hover:bg-ink-800 disabled:opacity-60 text-white font-bold text-sm rounded-md transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting to checkout...
                  </>
                ) : (
                  <>
                    Pay {priceLabel} & continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-ink-400">
              <span>Cancel anytime</span>
              <span>·</span>
              <span>No setup fees</span>
              <span>·</span>
              <span>Secure payment by Stripe</span>
            </div>
            <div className="mt-4 flex items-start gap-2.5 p-3 rounded-md bg-green-50 border border-green-100">
              <Shield className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700 leading-relaxed">
                <span className="font-semibold">7-day money-back guarantee.</span> Not happy? Email{' '}
                <a href="mailto:carehire@outlook.com" className="font-semibold underline">carehire@outlook.com</a>{' '}
                within one week for a full refund.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
